/**
 * Shared helper to compute emoji positions for Voronoi maze entry/exit regions.
 * Used by both the renderer (drawRegionEmoji, toSVG) and completion check.
 *
 * Algorithm: pole of inaccessibility of the region ∩ silhouette (the point with
 * the most clearance from walls and contour) + that clearance as the marker's
 * size budget. See computeEmojiPlacement.
 */

import type { Position, VoronoiRegion } from '../types/maze-types';

/**
 * Ray-casting point-in-polygon test.
 */
function isPointInsideContour(px: number, py: number, contour: Position[]): boolean {
  const n = contour.length;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = contour[i].x;
    const yi = contour[i].y;
    const xj = contour[j].x;
    const yj = contour[j].y;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Compute vertex centroid of a region.
 */
export function computeCentroid(region: VoronoiRegion): Position {
  const verts = region.vertices;
  if (verts.length === 0) return region.site;
  let sx = 0;
  let sy = 0;
  for (const v of verts) {
    sx += v.x;
    sy += v.y;
  }
  return { x: sx / verts.length, y: sy / verts.length };
}

export interface EmojiPlacement extends Position {
  /** Radius of the largest circle around (x, y) that stays inside the region
   *  (and the silhouette): the marker's size budget. */
  radius: number;
}

function pointInPolygon(px: number, py: number, poly: Position[]): boolean {
  return isPointInsideContour(px, py, poly);
}

/** Distance from a point to a segment. */
function distToSegment(px: number, py: number, a: Position, b: Position): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - a.x) * dx + (py - a.y) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Min distance from a point to any edge of a closed polygon. */
function clearance(px: number, py: number, poly: Position[]): number {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = distToSegment(px, py, poly[i], poly[(i + 1) % poly.length]);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Where (and how big) an entry/exit marker goes: the point of the region with
 * the most clearance from the region's own walls AND from the silhouette
 * contour, restricted to points inside both. A pole-of-inaccessibility search
 * over the centroid, the site and a coarse grid of the region's bbox — a few
 * hundred cheap tests per marker. `radius` is that clearance, so a marker
 * drawn within it never covers a wall and never pokes out of the shape (the
 * old centroid + fixed 22 px emoji did both on small / edge cells).
 *
 * Shared by the renderer (canvas + SVG) and the completion check, so the
 * "reach the exit" target is exactly where the emoji is drawn.
 */
export function computeEmojiPlacement(
  region: VoronoiRegion,
  allRegions: VoronoiRegion[],
  contour?: Position[],
): EmojiPlacement {
  void allRegions;
  const poly = region.vertices;
  const hasContour = !!contour && contour.length > 2;
  const inside = (x: number, y: number) =>
    (poly.length < 3 || pointInPolygon(x, y, poly)) && (!hasContour || isPointInsideContour(x, y, contour!));
  const score = (x: number, y: number) => {
    let c = poly.length >= 3 ? clearance(x, y, poly) : Infinity;
    if (hasContour) c = Math.min(c, clearance(x, y, contour!));
    return c;
  };

  const centroid = computeCentroid(region);
  const candidates: Position[] = [centroid, region.site];
  if (poly.length >= 3) {
    const xs = poly.map((v) => v.x);
    const ys = poly.map((v) => v.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const N = 9;
    for (let i = 1; i < N; i++) {
      for (let j = 1; j < N; j++) {
        candidates.push({ x: minX + ((maxX - minX) * i) / N, y: minY + ((maxY - minY) * j) / N });
      }
    }
  }

  let best: EmojiPlacement | null = null;
  for (const c of candidates) {
    if (!inside(c.x, c.y)) continue;
    const r = score(c.x, c.y);
    if (!best || r > best.radius) best = { x: c.x, y: c.y, radius: r };
  }
  if (best) return best;

  // Degenerate region (nothing inside both polygon and contour): fall back to
  // the old behaviour — centroid nudged toward the maze's centre of mass until
  // it is inside the contour.
  let cx = 0;
  let cy = 0;
  for (const r of allRegions) {
    const c = computeCentroid(r);
    cx += c.x;
    cy += c.y;
  }
  cx /= Math.max(1, allRegions.length);
  cy /= Math.max(1, allRegions.length);
  let x = centroid.x;
  let y = centroid.y;
  if (hasContour) {
    for (const f of [0.5, 0.75, 0.9]) {
      if (isPointInsideContour(x, y, contour!)) break;
      x = centroid.x + (cx - centroid.x) * f;
      y = centroid.y + (cy - centroid.y) * f;
    }
  }
  return { x, y, radius: 0 };
}

/**
 * The emoji position for a region (see computeEmojiPlacement) — the point the
 * renderer draws at and the completion check measures from.
 */
export function computeEmojiPosition(
  region: VoronoiRegion,
  allRegions: VoronoiRegion[],
  contour?: Position[],
): Position {
  const { x, y } = computeEmojiPlacement(region, allRegions, contour);
  return { x, y };
}

export interface BadgePlacement {
  /** Badge centre — just OUTSIDE the silhouette, nearest to the room. */
  x: number;
  y: number;
  /** Where the leader line ends (the room's marker point). */
  anchor: Position;
  /** Badge radius (px). */
  radius: number;
}

/**
 * Where the entry / exit BADGE goes: a fixed-size pin outside the silhouette,
 * at the contour point nearest to the room, pushed outward along the
 * centroid→contour direction and clamped to the canvas. The room itself is
 * marked by its tint + coloured walls; the badge is what stays legible on a
 * phone with 10 px cells or over a photo — its size never depends on the cell.
 * A dashed leader joins the badge to the room's marker point.
 */
export function computeBadgePlacement(
  region: VoronoiRegion,
  allRegions: VoronoiRegion[],
  contour: Position[] | undefined,
  canvas: { width: number; height: number },
  radius = 20,
): BadgePlacement {
  const anchor = computeEmojiPosition(region, allRegions, contour);
  if (!contour || contour.length < 3) {
    return { x: anchor.x, y: anchor.y, anchor, radius };
  }
  let cx = 0;
  let cy = 0;
  for (const p of contour) {
    cx += p.x;
    cy += p.y;
  }
  cx /= contour.length;
  cy /= contour.length;
  // nearest contour point to the room
  let best = contour[0];
  let bestD = Infinity;
  for (const p of contour) {
    const d = (p.x - anchor.x) ** 2 + (p.y - anchor.y) ** 2;
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  const gap = radius + 14;
  const clampX = (v: number) => Math.max(radius + 2, Math.min(canvas.width - radius - 2, v));
  const clampY = (v: number) => Math.max(radius + 2, Math.min(canvas.height - radius - 2, v));
  // Candidate directions: the local outward normal (room → its nearest contour
  // point) first, then away from the shape centroid, each fanned out ±30/±60/
  // ±90°. The first spot that sits OUTSIDE the silhouette with the whole disc
  // clear of every contour edge wins — the plain "away from centroid" push put
  // the badge over a thin limb (a cat's tail) whenever the room sat on its
  // inner side. Fallback = the centroid push, clamped, as before.
  const dirs: Array<[number, number]> = [];
  const pushDir = (dx: number, dy: number) => {
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    dirs.push([dx / len, dy / len]);
  };
  pushDir(best.x - anchor.x, best.y - anchor.y);
  pushDir(best.x - cx, best.y - cy);
  const fan: number[] = [0, 30, -30, 60, -60, 90, -90, 120, -120, 150, -150, 180];
  const fallback = dirs[dirs.length - 1] ?? [1, 0];
  for (const deg of fan) {
    const a = (deg * Math.PI) / 180;
    for (const [dx, dy] of dirs) {
      const rx = dx * Math.cos(a) - dy * Math.sin(a);
      const ry = dx * Math.sin(a) + dy * Math.cos(a);
      const x = clampX(best.x + rx * gap);
      const y = clampY(best.y + ry * gap);
      if (pointInPolygon(x, y, contour)) continue;
      if (clearance(x, y, contour) < radius + 4) continue;
      return { x, y, anchor, radius };
    }
  }
  const x = clampX(best.x + fallback[0] * gap);
  const y = clampY(best.y + fallback[1] * gap);
  return { x, y, anchor, radius };
}
